import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { matchesApi, clubsApi, championshipsApi } from '../lib/api';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import {
  Calendar,
  Clock,
  CheckCircle,
  Lock,
  Trophy,
  X,
} from 'lucide-react';
import NeonConfetti from '../components/NeonConfetti';
import MatchCard from './matches/MatchCard';
import MatchReportDialog from './matches/MatchReportDialog';
import JollyDialog from './matches/JollyDialog';
import EditReportDialog from './matches/EditReportDialog';
import ResolveDisputeDialog from './matches/ResolveDisputeDialog';
import ClubRosterDialog from '../components/ClubRosterDialog';

const isTournamentChamp = (champ) => {
  if (!champ) return false;
  return (champ.num_groups || 1) > 1 || champ.tournament_type === 'knockout' || champ.tournament_type === 'groups_with_knockout';
};

const Matches = ({ mode = 'championship' }) => {
  const { t, i18n } = useTranslation();
  const isItalian = i18n.language === 'it';
  const { user, isCaptain } = useAuth();
  const isTournamentMode = mode === 'tournament';

  // Core data
  const [matches, setMatches] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [homePlayers, setHomePlayers] = useState([]);
  const [awayPlayers, setAwayPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reportsLocked, setReportsLocked] = useState(false);
  const [championships, setChampionships] = useState([]);
  const [selectedChampionship, setSelectedChampionship] = useState('');

  // Report dialog
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [reportForm, setReportForm] = useState({ home_score: 0, away_score: 0, events: [], photos: [] });
  const photoInputRef = React.useRef(null);
  const [submittingReport, setSubmittingReport] = useState(false);
  const [cancellingReport, setCancellingReport] = useState(null);
  const [showConfetti, setShowConfetti] = useState(false);

  // Jolly dialog
  const [jollyMatch, setJollyMatch] = useState(null);
  const [jollyDate, setJollyDate] = useState('');
  const [jollyTime, setJollyTime] = useState('18:00');
  const [jollyLoading, setJollyLoading] = useState(false);
  const [jollyInfo, setJollyInfo] = useState(null);
  const [jollyClubId, setJollyClubId] = useState('');

  // Dispute dialog
  const [disputeMatch, setDisputeMatch] = useState(null);
  const [resolveLoading, setResolveLoading] = useState(false);
  const [resolveForm, setResolveForm] = useState({ home_score: 0, away_score: 0, accepted_report_index: null });

  // Edit dialog
  const [editingMatch, setEditingMatch] = useState(null);
  const [editForm, setEditForm] = useState({ home_score: 0, away_score: 0, events: [] });
  const [editLoading, setEditLoading] = useState(false);
  const [editHomePlayers, setEditHomePlayers] = useState([]);
  const [editAwayPlayers, setEditAwayPlayers] = useState([]);

  // Lightbox
  const [lightboxPhoto, setLightboxPhoto] = useState(null);

  // Club roster dialog
  const [rosterClub, setRosterClub] = useState(null);

  const isSuperAdmin = user?.role === 'super_admin';
  // For captains/vice-captains use the club they actually captain rather than
  // user.club_id which can get stale after transfers/replacements.
  const captainClub = clubs.find(c => c.captain_id === user?.user_id || c.vice_captain_id === user?.user_id || c.vice_captain_id_2 === user?.user_id);
  const userClubId = captainClub?.club_id || user?.club_id;

  // ---------- Data fetching ----------
  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      setLoading(true);
      try {
        const matchParams = {};
        if (!isSuperAdmin && userClubId) matchParams.club_id = userClubId;
        const [matchesRes, clubsRes, lockRes, champsRes] = await Promise.all([
          matchesApi.list(matchParams),
          clubsApi.list(),
          matchesApi.getReportsLock(),
          championshipsApi.list(),
        ]);
        if (!cancelled) {
          setMatches(matchesRes.data || []);
          setClubs(clubsRes.data || []);
          setReportsLocked(lockRes.data?.locked || false);
          const allChamps = champsRes.data || [];
          const filteredChamps = allChamps.filter(c => isTournamentMode ? isTournamentChamp(c) : !isTournamentChamp(c));
          setChampionships(filteredChamps);
          if (!selectedChampionship && filteredChamps.length > 0) {
            const active = filteredChamps.find(c => c.status === 'in_progress') || filteredChamps[0];
            setSelectedChampionship(active.championship_id);
          } else if (selectedChampionship && !filteredChamps.some(c => c.championship_id === selectedChampionship)) {
            // If currently selected champ doesn't belong to this mode, reset
            const fallback = filteredChamps.find(c => c.status === 'in_progress') || filteredChamps[0];
            setSelectedChampionship(fallback ? fallback.championship_id : '');
          }
        }
      } catch (error) {
        console.error('Error fetching matches:', error);
        if (!cancelled) { setMatches([]); setClubs([]); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => { cancelled = true; };
  }, [isSuperAdmin, userClubId, isTournamentMode]);

  useEffect(() => {
    const loadMatchPlayers = async () => {
      if (!selectedMatch) { setHomePlayers([]); setAwayPlayers([]); return; }
      try {
        const [homeRes, awayRes] = await Promise.all([
          clubsApi.getMembers(selectedMatch.home_club_id),
          clubsApi.getMembers(selectedMatch.away_club_id),
        ]);
        setHomePlayers(homeRes.data);
        setAwayPlayers(awayRes.data);
      } catch (error) {
        console.error('Error loading players:', error);
      }
    };
    loadMatchPlayers();
  }, [selectedMatch]);

  // ---------- Filtering ----------
  // Restrict matches to championships that match the current mode
  // (championship vs tournament). Without this, when the user picks
  // "Tutti i campionati" the tournament matches would leak into
  // /matches and vice-versa.
  const championshipIdsForMode = new Set(championships.map(c => c.championship_id));
  const matchesForMode = matches.filter(m => championshipIdsForMode.has(m.championship_id));
  const filteredMatches = selectedChampionship
    ? matchesForMode.filter(m => m.championship_id === selectedChampionship)
    : matchesForMode;
  // Hide knockout matches whose opponent is still TBD (bracket slots waiting for
  // the previous round winner). They're re-surfaced only after both teams are set.
  const isPlayableKnockout = (m) => {
    if (m.phase !== 'knockout' && !m.round) return true;
    return !!m.home_club_id && !!m.away_club_id;
  };
  const upcomingMatches = filteredMatches.filter(m =>
    (m.status === 'scheduled' || m.status === 'pending_report' || m.status === 'disputed')
    && isPlayableKnockout(m)
  );
  const completedMatches = filteredMatches.filter(m => m.status === 'completed');

  const groupByMatchDay = (matchList) => {
    const groups = {};
    matchList.forEach(m => {
      const day = m.match_day || 0;
      const leg = m.leg || 'andata';
      if (!groups[day]) groups[day] = { leg, matches: [] };
      groups[day].matches.push(m);
    });
    return Object.entries(groups)
      .sort(([a], [b]) => Number(a) - Number(b))
      .map(([day, data]) => ({ day: Number(day), leg: data.leg, matches: data.matches }));
  };

  const upcomingGrouped = groupByMatchDay(upcomingMatches);
  const completedGrouped = groupByMatchDay(completedMatches);

  const allMatchDays = [...new Set(filteredMatches.map(m => m.match_day || 0))].sort((a, b) => a - b);
  const [selectedMatchDay, setSelectedMatchDay] = useState('all');

  const filterByDay = (groups) => {
    if (selectedMatchDay === 'all') return groups;
    return groups.filter(g => g.day === Number(selectedMatchDay));
  };

  // ---------- Refresh helper ----------
  const refreshMatches = async () => {
    const params = isSuperAdmin ? {} : (userClubId ? { club_id: userClubId } : {});
    const res = await matchesApi.list(params);
    setMatches(res.data);
  };

  // ---------- Handlers ----------
  const handleSubmitReport = async () => {
    if (!selectedMatch) return;
    const homeScore = typeof reportForm.home_score === 'number' ? reportForm.home_score : parseInt(reportForm.home_score) || 0;
    const awayScore = typeof reportForm.away_score === 'number' ? reportForm.away_score : parseInt(reportForm.away_score) || 0;
    setSubmittingReport(true);
    try {
      const res = await matchesApi.submitReport(selectedMatch.match_id, {
        home_score: homeScore,
        away_score: awayScore,
        events: reportForm.events.filter(e => e.player_id).map(e => ({ ...e, minute: typeof e.minute === 'number' && !isNaN(e.minute) ? e.minute : 1 })),
        photos: reportForm.photos.length > 0 ? reportForm.photos : null,
      });
      toast.success(res.data?.message || (isItalian ? 'Report inviato con successo!' : 'Match report submitted!'));
      setSelectedMatch(null);
      if (res.data?.status === 'completed') setShowConfetti(true);
      await refreshMatches();
    } catch (error) {
      const detail = error.response?.data?.detail;
      let msg = isItalian ? 'Errore invio report' : 'Error submitting report';
      if (typeof detail === 'string') msg = detail;
      else if (Array.isArray(detail)) msg = detail.map(d => d.msg || JSON.stringify(d)).join(', ');
      toast.error(msg);
    } finally {
      setSubmittingReport(false);
    }
  };

  const handleCancelReport = async (matchId) => {
    if (!window.confirm(isItalian ? 'Sei sicuro di voler annullare questo report? La partita tornerà allo stato "programmata" e le statistiche verranno annullate.' : 'Are you sure you want to cancel this report?')) return;
    setCancellingReport(matchId);
    try {
      const res = await matchesApi.cancelReport(matchId);
      toast.success(res.data.message || (isItalian ? 'Report annullato!' : 'Report cancelled!'));
      await refreshMatches();
    } catch (error) {
      toast.error(error.response?.data?.detail || (isItalian ? 'Errore annullamento report' : 'Error cancelling report'));
    }
    setCancellingReport(null);
  };

  // Compress/resize an image file before base64 encoding, to keep the report payload
  // well under the MongoDB 16MB BSON limit and the proxy body-size limit. We downscale
  // to max 1600px on the long side and re-encode as JPEG 70%.
  const compressImage = (file) => new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();
    reader.onload = (ev) => { img.src = ev.target.result; };
    reader.onerror = reject;
    img.onload = () => {
      const MAX = 1600;
      let { width, height } = img;
      if (width > MAX || height > MAX) {
        if (width >= height) { height = Math.round(height * (MAX / width)); width = MAX; }
        else { width = Math.round(width * (MAX / height)); height = MAX; }
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL('image/jpeg', 0.7));
    };
    img.onerror = reject;
    reader.readAsDataURL(file);
  });

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    const remaining = 5 - reportForm.photos.length;
    if (remaining <= 0) { toast.error(isItalian ? 'Massimo 5 foto per report' : 'Maximum 5 photos per report'); return; }
    const slice = files.slice(0, remaining);
    for (const file of slice) {
      if (file.size > 15 * 1024 * 1024) {
        toast.error(isItalian ? `${file.name} è troppo grande (max 15MB)` : `${file.name} is too large (max 15MB)`);
        continue;
      }
      try {
        const compressed = await compressImage(file);
        setReportForm(prev => ({ ...prev, photos: [...prev.photos, compressed] }));
      } catch (err) {
        toast.error(isItalian ? `Errore elaborazione ${file.name}` : `Error processing ${file.name}`);
      }
    }
    if (photoInputRef.current) photoInputRef.current.value = '';
  };

  const removePhoto = (index) => {
    setReportForm(prev => ({ ...prev, photos: prev.photos.filter((_, i) => i !== index) }));
  };

  const openEditReport = async (match) => {
    setEditingMatch(match);
    setEditForm({ home_score: match.home_score || 0, away_score: match.away_score || 0, events: (match.events || []).map(e => ({ ...e })) });
    try {
      const [homeRes, awayRes] = await Promise.all([clubsApi.getMembers(match.home_club_id), clubsApi.getMembers(match.away_club_id)]);
      setEditHomePlayers(homeRes.data);
      setEditAwayPlayers(awayRes.data);
    } catch (err) { console.error('Error loading players for edit:', err); }
  };

  const handleSaveEdit = async () => {
    if (!editingMatch) return;
    setEditLoading(true);
    try {
      const cleanEvents = editForm.events.filter(e => e.player_id).map(e => ({
        player_id: e.player_id, player_name: e.player_name || '', event_type: e.event_type,
        minute: typeof e.minute === 'number' && !isNaN(e.minute) ? e.minute : 1,
      }));
      const res = await matchesApi.editReport(editingMatch.match_id, { home_score: editForm.home_score, away_score: editForm.away_score, events: cleanEvents });
      toast.success(res.data?.message || (isItalian ? 'Report aggiornato!' : 'Report updated!'));
      setEditingMatch(null);
      await refreshMatches();
    } catch (err) {
      toast.error(err.response?.data?.detail || (isItalian ? 'Errore salvataggio' : 'Save error'));
    } finally { setEditLoading(false); }
  };

  const handleRequestJolly = async () => {
    if (!jollyMatch || !jollyDate) return;
    if (isSuperAdmin && !jollyClubId) { toast.error(isItalian ? 'Seleziona per quale squadra usare il Jolly' : 'Select which club uses the Jolly'); return; }
    setJollyLoading(true);
    try {
      const proposedDate = new Date(`${jollyDate}T${jollyTime}:00`).toISOString();
      const res = await matchesApi.requestJolly(jollyMatch.match_id, proposedDate, isSuperAdmin ? jollyClubId : undefined);
      toast.success(res.data?.message || (isItalian ? 'Richiesta Jolly inviata!' : 'Jolly request sent!'));
      setJollyMatch(null); setJollyDate(''); setJollyTime('18:00'); setJollyClubId('');
    } catch (error) {
      toast.error(error.response?.data?.detail || (isItalian ? 'Errore invio Jolly' : 'Jolly request error'));
    }
    setJollyLoading(false);
  };

  const handleResolveDispute = async () => {
    if (!disputeMatch) return;
    setResolveLoading(true);
    try {
      const res = await matchesApi.resolveDispute(disputeMatch.match_id, resolveForm);
      toast.success(res.data?.message || (isItalian ? 'Contestazione risolta!' : 'Dispute resolved!'));
      setDisputeMatch(null);
      await refreshMatches();
    } catch (err) {
      toast.error(err.response?.data?.detail || (isItalian ? 'Errore risoluzione' : 'Resolution error'));
    } finally { setResolveLoading(false); }
  };

  // ---------- MatchCard callbacks ----------
  const onOpenReport = (match) => {
    setSelectedMatch(match);
    setReportForm({ home_score: 0, away_score: 0, events: [], photos: [] });
  };

  const onRequestJolly = async (match) => {
    setJollyMatch(match);
    setJollyDate(''); setJollyTime('18:00'); setJollyInfo(null);
    try { const res = await matchesApi.getJollyInfo(match.match_id); setJollyInfo(res.data); } catch (e) { console.error(e); }
  };

  const onResolveDispute = (match) => {
    setDisputeMatch(match);
    setResolveForm({ home_score: 0, away_score: 0, accepted_report_index: null });
  };

  // ---------- Shared card props ----------
  const handleReportSynced = async (result) => {
    if (result?.error) {
      toast.error(result.error);
      return;
    }
    const msg = result?.message || (result?.status === 'completed'
      ? (isItalian ? 'Report confermato!' : 'Report confirmed!')
      : (isItalian ? 'Stato ricalcolato' : 'Status resynced'));
    toast.success(msg);
    await refreshMatches();
  };

  const cardProps = {
    clubs,
    user,
    isSuperAdmin,
    isCaptain,
    userClubId,
    reportsLocked,
    isItalian,
    cancellingReport,
    isTournament: isTournamentMode,
    onOpenReport,
    onEditReport: openEditReport,
    onCancelReport: handleCancelReport,
    onRequestJolly,
    onResolveDispute,
    onReportSynced: handleReportSynced,
    onLightbox: setLightboxPhoto,
    onClubClick: (club) => club && setRosterClub(club),
    champNames: Object.fromEntries(championships.map(c => [c.championship_id, c.name])),
  };

  // ---------- Match day group renderer ----------
  const MatchDayGroup = ({ group, accentColor, testIdPrefix = '' }) => (
    <div data-testid={`matchday-group-${testIdPrefix}${group.day}`}>
      <div className="flex items-center gap-3 mb-3">
        <div className={`h-px flex-1 bg-gradient-to-r from-[${accentColor}]/30 to-transparent`} />
        <span className={`text-xs font-bold uppercase tracking-widest text-[${accentColor}] whitespace-nowrap`}>
          {group.leg === 'ritorno' ? 'Ritorno' : 'Andata'} — {isItalian ? 'Giornata' : 'Matchday'} {group.day}
        </span>
        <div className={`h-px flex-1 bg-gradient-to-l from-[${accentColor}]/30 to-transparent`} />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {group.matches.map(match => (
          <MatchCard key={match.match_id} match={match} showReport={!testIdPrefix} {...cardProps} />
        ))}
      </div>
    </div>
  );

  // ---------- Render ----------
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-[#00F0FF] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="flex items-center gap-3">
        <Calendar size={24} className="text-[#00F0FF]" />
        <h1 className="font-heading text-2xl md:text-3xl font-bold uppercase tracking-tight text-white">
          {isTournamentMode
            ? (isItalian ? 'Partite Tornei' : 'Tournament Matches')
            : t('matches.title')}
        </h1>
      </motion.div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3" data-testid="filters-bar">
        {championships.length > 1 && (
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <Trophy size={16} className="text-[#FFEA00] shrink-0" />
            <select
              value={selectedChampionship}
              onChange={(e) => { setSelectedChampionship(e.target.value); setSelectedMatchDay('all'); }}
              className="bg-[#0a0d15] border border-white/10 text-white px-3 py-2 text-sm rounded-lg w-full sm:w-auto"
              data-testid="championship-select"
            >
              <option value="">{isTournamentMode ? (isItalian ? 'Tutti i tornei' : 'All tournaments') : (isItalian ? 'Tutti i campionati' : 'All championships')}</option>
              {championships.filter(c => c.status === 'in_progress' || c.status === 'completed').map(c => (
                <option key={c.championship_id} value={c.championship_id}>{c.name}</option>
              ))}
            </select>
          </div>
        )}
        {allMatchDays.length > 1 && (
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <Calendar size={16} className="text-[#00F0FF] shrink-0" />
            <select
              value={selectedMatchDay}
              onChange={(e) => setSelectedMatchDay(e.target.value)}
              className="bg-[#0a0d15] border border-white/10 text-white px-3 py-2 text-sm rounded-lg w-full sm:w-auto"
              data-testid="matchday-select"
            >
              <option value="all">{isItalian ? 'Tutte le giornate' : 'All matchdays'}</option>
              {allMatchDays.map(day => {
                const match = filteredMatches.find(m => (m.match_day || 0) === day);
                const leg = match?.leg === 'ritorno' ? 'Ritorno' : 'Andata';
                return <option key={day} value={day}>{`${leg} - ${isItalian ? 'Giornata' : 'Matchday'} ${day}`}</option>;
              })}
            </select>
          </div>
        )}
      </div>

      <Tabs defaultValue="upcoming" className="space-y-6">
        {reportsLocked && (
          <div className="flex items-center gap-3 p-3 bg-[#FF1744]/10 border border-[#FF1744]/30 rounded text-[#FF1744] text-sm" data-testid="reports-locked-banner">
            <Lock size={16} className="shrink-0" />
            <span className="font-medium">
              {isItalian ? "L'invio dei report è attualmente bloccato dall'amministrazione." : 'Match report submissions are currently locked by administration.'}
            </span>
          </div>
        )}
        <TabsList className="bg-[#0a0d15] border border-white/10 p-1 inline-flex">
          <TabsTrigger value="upcoming" className="data-[state=active]:bg-[#00F0FF] data-[state=active]:text-white uppercase tracking-wider text-[10px] sm:text-xs font-bold whitespace-nowrap px-2.5 sm:px-4" data-testid="tab-upcoming">
            <Clock size={14} className="mr-1.5" />
            {t('matches.upcoming')} ({upcomingMatches.length})
          </TabsTrigger>
          <TabsTrigger value="completed" className="data-[state=active]:bg-[#00F0FF] data-[state=active]:text-white uppercase tracking-wider text-[10px] sm:text-xs font-bold whitespace-nowrap px-2.5 sm:px-4" data-testid="tab-completed">
            <CheckCircle size={14} className="mr-1.5" />
            {t('matches.completed')} ({completedMatches.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="upcoming">
          {filterByDay(upcomingGrouped).length > 0 ? (
            <div className="space-y-6">
              {filterByDay(upcomingGrouped).map(group => (
                <MatchDayGroup key={group.day} group={group} accentColor="#00F0FF" />
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-[#0a0d15] border border-white/10">
              <Calendar size={36} className="mx-auto text-slate-600 mb-4" />
              <p className="text-slate-500">{t('common.noData')}</p>
            </div>
          )}
        </TabsContent>

        <TabsContent value="completed">
          {filterByDay(completedGrouped).length > 0 ? (
            <div className="space-y-6">
              {filterByDay(completedGrouped).map(group => (
                <MatchDayGroup key={group.day} group={group} accentColor="#00E676" testIdPrefix="completed-" />
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-[#0a0d15] border border-white/10">
              <CheckCircle size={48} className="mx-auto text-slate-600 mb-4" />
              <p className="text-slate-500">{t('common.noData')}</p>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Dialogs */}
      <MatchReportDialog
        selectedMatch={selectedMatch}
        clubs={clubs}
        homePlayers={homePlayers}
        awayPlayers={awayPlayers}
        isSuperAdmin={isSuperAdmin}
        userClubId={userClubId}
        isItalian={isItalian}
        reportForm={reportForm}
        setReportForm={setReportForm}
        submittingReport={submittingReport}
        photoInputRef={photoInputRef}
        onSubmit={handleSubmitReport}
        onClose={() => setSelectedMatch(null)}
        onPhotoUpload={handlePhotoUpload}
        onRemovePhoto={removePhoto}
      />

      <JollyDialog
        jollyMatch={jollyMatch}
        clubs={clubs}
        isSuperAdmin={isSuperAdmin}
        isItalian={isItalian}
        jollyDate={jollyDate}
        setJollyDate={setJollyDate}
        jollyTime={jollyTime}
        setJollyTime={setJollyTime}
        jollyClubId={jollyClubId}
        setJollyClubId={setJollyClubId}
        jollyInfo={jollyInfo}
        jollyLoading={jollyLoading}
        onSubmit={handleRequestJolly}
        onClose={() => setJollyMatch(null)}
      />

      <EditReportDialog
        editingMatch={editingMatch}
        clubs={clubs}
        editHomePlayers={editHomePlayers}
        editAwayPlayers={editAwayPlayers}
        editForm={editForm}
        setEditForm={setEditForm}
        editLoading={editLoading}
        isItalian={isItalian}
        onSave={handleSaveEdit}
        onClose={() => setEditingMatch(null)}
      />

      <ResolveDisputeDialog
        disputeMatch={disputeMatch}
        clubs={clubs}
        resolveForm={resolveForm}
        setResolveForm={setResolveForm}
        resolveLoading={resolveLoading}
        isItalian={isItalian}
        onResolve={handleResolveDispute}
        onClose={() => setDisputeMatch(null)}
      />

      {/* Club Roster Dialog */}
      {rosterClub && (
        <ClubRosterDialog
          clubId={rosterClub.club_id}
          clubName={rosterClub.name}
          clubLogo={rosterClub.logo}
          onClose={() => setRosterClub(null)}
        />
      )}

      {/* Confetti */}
      <NeonConfetti active={showConfetti} duration={4000} particleCount={120} onComplete={() => setShowConfetti(false)} />

      {/* Photo Lightbox */}
      {lightboxPhoto && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4" onClick={() => setLightboxPhoto(null)} data-testid="photo-lightbox">
          <button className="absolute top-4 right-4 text-white/70 hover:text-white z-10" onClick={() => setLightboxPhoto(null)} data-testid="lightbox-close">
            <X size={28} />
          </button>
          <img src={lightboxPhoto} alt="Foto partita" className="max-w-full max-h-[90vh] object-contain rounded" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
};

export default Matches;
